import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { UpdateSourceExtIdStartDateAndStageDocument } from '~app/graph/generated/graphql'
import CSVImporter, {
  parseCSVDate,
} from '../../components/Elements/SourcesBulkCSVImporter'

/*
 * CSV file schema
 */

interface CSVColumn {
  id: string
  startDate: string
  endDate?: string
  externalId: string
}

const columnSchema = z.object({
  externalId: z.string().min(1),
  id: z.string().min(1),
  startDate: z.string().min(1),
})

/**
 * defines how data is extracted from CSV rows by the column name
 */
const parseDataFromRow = (row: unknown): CSVColumn => {
  const parsedData = {
    id: row['Forward ID'],
    startDate: parseCSVDate(row['Startdatum']),
    endDate: row['Enddatum'],
    externalId: row['Auftragsnummer'],
  }
  return parsedData
}

/*
 * Component
 */

const UploadCSVButton = ({
  onUploadComplete,
  onError,
}: {
  onUploadComplete: VoidFunction
  onError: (error: Error, durationSeconds?: number) => void
}) => {
  const { t } = useTranslation()

  const [updateSourceExtIdStartDateAndStage] = useMutation(
    UpdateSourceExtIdStartDateAndStageDocument
  )

  return (
    <CSVImporter
      parseDataFromRow={parseDataFromRow}
      schema={columnSchema}
      buttonLabel={t('sources.uploadCSVButton')}
      onParsed={(columns: CSVColumn[]) =>
        columns.map(({ id, startDate, externalId }) =>
          updateSourceExtIdStartDateAndStage({
            variables: {
              id,
              startAt: startDate,
              externalID: externalId,
            },
          })
            .then(mutationResult => {
              if (
                !mutationResult ||
                mutationResult.error ||
                !mutationResult.data
              ) {
                return {
                  error:
                    mutationResult && mutationResult.error
                      ? mutationResult.error.message
                      : 'mutation failed',
                  sourceId: id,
                }
              }
              onUploadComplete()

              return null
            })
            .catch(error => {
              onError(error, 3)
              return { error, sourceId: id }
            })
        )
      }
      onFailure={onError}
    />
  )
}

export default UploadCSVButton
